import React, {useEffect, useRef} from 'react';
import {
  ArrowRightIcon,
  CodeBracketIcon,
  CpuChipIcon,
  CircleStackIcon,
  ChartBarIcon,
  RocketLaunchIcon,
  UserGroupIcon,
  Square3Stack3DIcon,
  Cog6ToothIcon,
  CheckIcon,
} from '@heroicons/react-v2/24/outline';
import {SparklesIcon as SparklesSolidIcon} from '@heroicons/react-v2/16/solid';
import {useAnalytics} from '../hooks';
import Button, {ButtonSizes} from '../components/ButtonNew';
import UniversityBar from '../components/HomepageUniversityBar';
import DocumentHeader from '../components/DocumentHeader';
import {
  ChatPreview,
  InterviewPreview,
  CoursePreview,
  PracticeMock,
  ExperienceStack,
  JobsMock,
  CoachingPreview,
  ResumesPreview,
  GuidePreview,
} from '../components/homepage/ProductPreview';
import {
  Reveal,
  CompanyFeed,
  Community,
  AgentSection,
  Pricing,
  LinkArrow,
  primaryButton,
  secondaryButton,
  textLink,
  eyebrow,
} from '../components/homepage/HomepageSections';

export const CtaSection = ({text, href, cta}) => (
  <div className="px-4 py-12">
    <h2 className="text-center text-xl md:text-2xl font-bold text-black">{text}</h2>
    <div className="flex justify-center mt-6">
      <Button size={ButtonSizes.LARGE} href={href} className="w-full sm:w-auto">
        {cta}
      </Button>
    </div>
  </div>
);

const roles = [
  {
    name: 'Product Management',
    slug: 'pm',
    icon: Square3Stack3DIcon,
    tile: 'bg-indigo-50 text-indigo-600 group-hover:bg-indigo-600',
  },
  {
    name: 'Software Engineering',
    slug: 'software-engineering',
    icon: CodeBracketIcon,
    tile: 'bg-sky-50 text-sky-600 group-hover:bg-sky-600',
  },
  {
    name: 'Engineering Management',
    slug: 'engineering-management',
    icon: UserGroupIcon,
    tile: 'bg-amber-50 text-amber-600 group-hover:bg-amber-500',
  },
  {
    name: 'Data\nScience',
    slug: 'data-science',
    icon: ChartBarIcon,
    tile: 'bg-emerald-50 text-emerald-600 group-hover:bg-emerald-600',
  },
  {
    name: 'Data Engineering',
    slug: 'data-engineering',
    icon: CircleStackIcon,
    tile: 'bg-cyan-50 text-cyan-600 group-hover:bg-cyan-600',
  },
  {
    name: 'Machine Learning',
    slug: 'ml-engineer',
    icon: CpuChipIcon,
    tile: 'bg-violet-50 text-violet-600 group-hover:bg-violet-600',
  },
  {
    name: 'Forward Deployed',
    slug: 'forward-deployed-engineering',
    icon: RocketLaunchIcon,
    tile: 'bg-rose-50 text-rose-500 group-hover:bg-rose-500',
  },
  {
    name: 'Technical Program Management',
    slug: 'tpm',
    icon: Cog6ToothIcon,
    tile: 'bg-fuchsia-50 text-fuchsia-600 group-hover:bg-fuchsia-600',
  },
];

const features = [
  {
    number: '01',
    label: 'Expert courses',
    title: 'Courses that help you get the job',
    body: 'Learn interview frameworks through video lessons from hiring managers and technical leads at top companies.',
    href: '/courses?src=homepage',
    cta: 'Explore courses',
    visual: <CoursePreview />,
    bg: 'bg-indigo-50/80',
  },
  {
    number: '02',
    label: 'Mock interviews',
    title: 'Realistic mock interviews',
    body: 'Practice out loud with an AI interviewer and get graded with real rubrics. Or book peer-to-peer mock interviews with other candidates preparing for the same role.',
    href: '/practice?src=homepage',
    cta: 'Try a mock interview',
    visual: <InterviewPreview />,
    bg: 'bg-stone-100',
  },
  {
    number: '03',
    label: 'Interview Questions',
    title: 'Practice questions from real interviews',
    body: 'Browse thousands of real questions by company and role, and watch real candidates answer them in our mock interview library.',
    href: '/questions?src=homepage',
    cta: 'Explore questions',
    visual: <PracticeMock />,
    bg: 'bg-slate-100',
  },
  {
    number: '04',
    label: 'Interview experiences',
    title: 'Learn from candidates who just interviewed',
    body: 'Read recent, firsthand reports from real interview loops: what was asked, how each round went, and what made the difference.',
    href: '/experiences?src=homepage',
    cta: 'Read experiences',
    visual: <ExperienceStack />,
    bg: 'bg-indigo-50/80',
  },
  {
    number: '05',
    label: 'Expert Coaching',
    title: 'Get coached by experts from top companies',
    body: 'Book a session with a vetted coach for mock interviews, resume feedback, negotiation help, or career advice.',
    href: '/coaching?src=homepage',
    cta: 'Find your coach',
    visual: <CoachingPreview />,
    bg: 'bg-slate-100',
  },
  {
    number: '06',
    label: 'Company guides',
    title: 'Get an inside look at the interview process',
    body: 'Expert guides break down each company’s full interview loop: every round, what’s asked, and how to prepare for it.',
    href: '/guides?src=homepage',
    cta: 'Explore company guides',
    visual: <GuidePreview />,
    bg: 'bg-stone-100',
  },
  {
    number: '07',
    label: 'AI Coach',
    title: 'Need help? Just ask Ace.',
    body: 'An interview prep AI backed by real data. Ask Ace to explain a concept, create a study plan, or prep you for a specific job interview.',
    href: '/chat?src=homepage',
    cta: 'Chat with Ace',
    visual: <ChatPreview />,
    bg: 'bg-indigo-50/80',
  },
  {
    number: '08',
    label: 'Resume gallery',
    title: 'See real resumes that landed the job',
    body: 'Browse real resumes that landed interviews and offers at top companies, explore career paths, and use templates to build your own.',
    href: '/resumes?src=homepage',
    cta: 'Browse the resume gallery',
    visual: <ResumesPreview />,
    bg: 'bg-slate-100',
  },
  {
    number: '09',
    label: 'Jobs & referrals',
    title: 'Find your next role and get referred',
    body: 'Browse job boards curated for your role and get referred by one of the 1,000+ referrers in our community.',
    href: '/jobs?src=homepage',
    cta: 'Explore job boards',
    visual: <JobsMock />,
    bg: 'bg-stone-100',
  },
];

const HomeLanding = () => {
  const {tracker} = useAnalytics();
  const viewed = useRef(false);
  useEffect(() => {
    if (!viewed.current) {
      tracker.homepageView();
      viewed.current = true;
    }
  }, [tracker]);

  return (
    <div className="overflow-x-clip bg-white text-gray-900" data-body-bg="white">
      <DocumentHeader
        title="Interview prep for product, engineering, data science, and more"
        metaDescription="Aced (formerly Exponent) helps you land your dream tech career with interview courses, thousands of real questions, AI-powered practice, and expert coaching."
        socialDescription="Aced (formerly Exponent) helps you land your dream tech career with interview courses, thousands of real questions, AI-powered practice, and expert coaching."
      />

      <section className="pt-10 text-center md:pt-14">
        <div className="mx-auto w-full max-w-6xl px-5">
          <a
            href="/practice?src=homepage"
            className="rise-in inline-flex items-center gap-2.5 rounded-full border border-indigo-100 bg-white px-4 py-2 text-sm font-medium text-gray-600 transition hover:border-indigo-200 hover:bg-indigo-50/40"
          >
            <SparklesSolidIcon className="h-4 w-4 text-indigo-500" />
            New: AI feedback on your interviews
            <ArrowRightIcon className="h-4 w-4" />
          </a>
          <h1 className="rise-in mx-auto mt-7 max-w-4xl text-balance text-[38px] font-semibold leading-[1.12] tracking-[-0.03em] text-gray-900 [animation-delay:0.1s] xs:text-[44px] sm:text-6xl sm:leading-[1.1] sm:tracking-[-0.04em] md:text-7xl">
            Everything you need to
            {' '}
            <span className="ace-sweep inline-block text-indigo-600">ace</span>
            {' '}
            your tech interviews
          </h1>
          <p className="rise-in mx-auto mt-6 max-w-xl text-pretty text-lg leading-relaxed text-gray-600 [animation-delay:0.2s] md:text-xl">
            Level up your career and land your next role with courses,
            mock interviews, and expert coaching.
          </p>
          <div className="rise-in mt-8 flex flex-col items-center justify-center gap-3.5 [animation-delay:0.3s] sm:flex-row">
            <a
              className={`${primaryButton} w-full sm:w-auto`}
              href="/signup?src=homepage"
              onClick={() => tracker.landingClickViewCourses()}
            >
              Get started for free
              <ArrowRightIcon className="h-4 w-4" />
            </a>
            <a className={`${secondaryButton} w-full sm:w-auto`} href="#explore">
              View courses
              <ArrowRightIcon className="h-4 w-4" />
            </a>
          </div>
          <p className="rise-in mt-5 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-sm text-gray-500 [animation-delay:0.38s]">
            <CheckIcon className="h-4 w-4 text-indigo-500" />
            Join over 1 million people on Aced
          </p>

          <div id="explore" className="mt-11 scroll-mt-28 text-left md:mt-12">
            <h2 className="sr-only">What are you preparing for?</h2>
            <div className="scrollbar-hidden -mx-5 mt-4 flex snap-x gap-3 overflow-x-auto px-5 pb-2 pt-1 scroll-pl-5 [mask-image:linear-gradient(90deg,transparent,black_28px,black_calc(100%-28px),transparent)]">
              {roles.map(({
                name, slug, icon: Icon, tile,
              }, i) => (
                <a
                  href={`/courses/${slug}`}
                  key={slug}
                  className="rise-in group flex w-32 shrink-0 snap-start flex-col items-center justify-center gap-2.5 rounded-xl border border-gray-200 px-3 py-4 text-center transition duration-200 hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-lg hover:shadow-gray-900/5 motion-reduce:hover:translate-y-0"
                  style={{animationDelay: `${0.5 + i * 0.05}s`}}
                  onClick={() => tracker.landingClickTrack({course: slug})}
                >
                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors duration-200 group-hover:text-white ${tile}`}>
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="whitespace-pre-line text-sm font-medium leading-snug text-gray-800">
                    {name}
                  </span>
                </a>
              ))}
              <a
                href="/courses?src=homepage"
                className="rise-in group flex w-32 shrink-0 snap-start flex-col items-center justify-center gap-2.5 rounded-xl border border-gray-200 bg-gray-50 px-3 py-4 text-center text-sm font-semibold text-gray-600 transition duration-200 hover:-translate-y-0.5 hover:border-gray-300 hover:text-gray-800 motion-reduce:hover:translate-y-0 [animation-delay:0.9s]"
                onClick={() => tracker.landingClickTrack({course: 'all'})}
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-200/70 text-gray-500 transition-colors duration-200 group-hover:bg-gray-600 group-hover:text-white">
                  <ArrowRightIcon className="h-5 w-5" />
                </span>
                Explore all roles
              </a>
            </div>
          </div>
        </div>
      </section>

      <CompanyFeed />

      <section className="mx-auto w-full max-w-6xl px-5 pt-6 md:pt-10">
        {features.map(({
          number, label, title, body, href, cta, visual, bg,
        }, i) => (
          <Reveal key={number} className="grid items-center gap-8 py-8 md:grid-cols-2 md:gap-20 md:py-14">
            <div className={`min-w-0 max-w-md ${i % 2 === 1 ? 'md:order-2' : ''}`}>
              <span className={eyebrow}>{label}</span>
              <h2 className="mt-3 text-balance text-3xl font-bold tracking-tight text-gray-900 md:text-4xl md:leading-[1.15]">
                {title}
              </h2>
              <p className="mb-6 mt-4 text-pretty text-base leading-relaxed text-gray-600 md:text-lg">
                {body}
              </p>
              <a className={textLink} href={href}>
                {cta}
                <LinkArrow />
              </a>
            </div>
            <div className={`min-w-0 rounded-3xl p-5 sm:p-10 ${bg} ${i % 2 === 1 ? 'md:order-1' : ''}`}>
              {visual}
            </div>
          </Reveal>
        ))}
      </section>

      <Community />

      <div className="mx-auto w-full max-w-6xl border-b border-gray-100 px-5">
        <UniversityBar />
      </div>

      <Pricing />

      <div className="pb-14 md:pb-24">
        <AgentSection />
      </div>

      <section className="bg-gray-50 py-16 text-center md:py-28">
        <Reveal className="mx-auto w-full max-w-6xl px-5">
          <h2 className="text-balance text-4xl font-semibold tracking-tight text-gray-900 md:text-5xl">
            Ready to start preparing?
          </h2>
          <p className="mt-5 text-pretty text-lg text-gray-600">
            Create a free account to explore courses and start practicing.
          </p>
          <div className="mt-8 flex justify-center">
            <a className={`${primaryButton} w-full sm:w-auto`} href="/signup?src=homepage">
              Get started for free
              <ArrowRightIcon className="h-4 w-4" />
            </a>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default HomeLanding;
